<template>
    <section class="bg-nomad-greenish px-6 pb-20 pt-16 text-nomad-primary sm:pb-28 sm:pt-20">
        <div class="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
            <div>
                <p class="w-fit rounded-full border border-nomad-primary/15 bg-white/45 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.16em] text-nomad-primary/70">Start a conversation</p>
                <h1 class="mt-6 max-w-3xl text-5xl font-extrabold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">Let&apos;s make the next move together.</h1>
                <p class="mt-6 max-w-2xl text-lg leading-relaxed text-nomad-primary/75 sm:text-xl">Tell us what you&apos;re building, what&apos;s stuck, or where you want to go. We&apos;ll help turn the first conversation into a clear next step.</p>
            </div>
            <div class="flex justify-center lg:justify-end">
                <img src="/assets/contact.svg" class="w-full max-w-[34rem] rounded-[1.5rem] p-2 shadow-[0_1rem_2.5rem_rgba(42,38,64,0.1)]" alt="Contact Nexa Interactives">
            </div>
        </div>
    </section>
    <Container class="grid grid-cols-1 gap-10 md:grid-cols-12 lg:gap-16">
        <div class="md:col-span-5">
            <p class="text-xs font-extrabold uppercase tracking-[0.16em] text-nomad-primary/60">Contact</p>
            <h2 class="mt-3 text-3xl font-extrabold tracking-tight text-nomad-primary sm:text-4xl">Tell us what you&apos;re building.</h2>
            <p class="mt-5 max-w-lg text-base leading-relaxed text-[#5F626D] sm:text-lg">Share your idea, challenge, or next milestone. We&apos;ll review the details and get back to you with a practical next step.</p>
            <div class="my-7 h-px w-20 bg-nomad-primary/20"></div>
            <ul class="space-y-5 text-sm text-[#5F626D] sm:text-base">
                <li class="flex items-center gap-3"><MapPin class="h-5 w-5 shrink-0 text-nomad-primary" aria-hidden="true" /> Washington, DC and Minneapolis, MN</li>
                <li class="flex items-center gap-3"><Phone class="h-5 w-5 shrink-0 text-nomad-primary" aria-hidden="true" /> +1 (202) 670-1716</li>
                <li class="flex items-center gap-3"><Mail class="h-5 w-5 shrink-0 text-nomad-primary" aria-hidden="true" /> support@nexainteractives.com</li>
            </ul>
        </div>
        <div class="md:col-span-7">
            <form action="#" v-on:submit.prevent="_submit">
                <div class="rounded-[1.5rem] border border-[#E5E4E1] bg-[#F7F7F5] p-6 shadow-[0_0.75rem_2rem_rgba(23,25,35,0.05)] sm:p-8">
                    <div class="mb-7">
                        <h2 class="text-2xl font-extrabold tracking-tight text-nomad-primary">Start the conversation</h2>
                        <p class="mt-2 text-sm leading-relaxed text-[#5F626D]">A few details are enough. We&apos;ll take it from there.</p>
                    </div>
                    <div class="grid gap-5 sm:grid-cols-2">
                    <div>
                        <label for="full-name" class="block text-sm font-bold text-nomad-primary">Full name <span class="text-red-600">*</span></label>
                        <input type="text" name="full-name" id="full-name" autocomplete="name" v-model="form.fullName" required placeholder="Your name" class="mt-2 block w-full rounded-xl border border-[#D9D9D6] bg-white px-4 py-3.5 text-base text-gray-900 outline-none transition placeholder:text-[#858391] focus:border-nomad-primary focus:ring-2 focus:ring-nomad-primary/15" />
                    </div>
                    <div>
                        <label for="email" class="block text-sm font-bold text-nomad-primary">Email <span class="text-red-600">*</span></label>
                        <input type="email" name="email" id="email" autocomplete="email" v-model="form.email" required placeholder="you@company.com" class="mt-2 block w-full rounded-xl border border-[#D9D9D6] bg-white px-4 py-3.5 text-base text-gray-900 outline-none transition placeholder:text-[#858391] focus:border-nomad-primary focus:ring-2 focus:ring-nomad-primary/15" />
                    </div>
                    <div>
                        <label for="phone" class="block text-sm font-bold text-nomad-primary">Phone <span class="text-red-600">*</span></label>
                        <input type="tel" name="phone" id="phone" autocomplete="tel" v-model="form.phone" required placeholder="+1 (000) 000-0000" class="mt-2 block w-full rounded-xl border border-[#D9D9D6] bg-white px-4 py-3.5 text-base text-gray-900 outline-none transition placeholder:text-[#858391] focus:border-nomad-primary focus:ring-2 focus:ring-nomad-primary/15" />
                    </div>
                    <div class="sm:col-span-2">
                        <label for="message" class="block text-sm font-bold text-nomad-primary">Message <span class="text-red-600">*</span></label>
                        <textarea name="message" id="message" v-model="form.message" required placeholder="What would you like to build?" rows="5" class="mt-2 block min-h-[8.5rem] w-full resize-y rounded-xl border border-[#D9D9D6] bg-white px-4 py-3.5 text-base text-gray-900 outline-none transition placeholder:text-[#858391] focus:border-nomad-primary focus:ring-2 focus:ring-nomad-primary/15"></textarea>
                    </div>
                    </div>
                    <div v-if="errors && errors.message">
                        <div class="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{{ errors.message }}</div>
                    </div>
                    <div v-if="message">
                        <div class="mt-5 rounded-xl bg-green-50 px-4 py-3 text-sm font-bold text-green-700">{{ message }}</div>
                    </div>
                    <div>
                        <button type="submit" :disabled="(!form.fullName || !form.email || !form.phone || !form.message) || loading" class="mt-6 inline-flex items-center gap-2 rounded-full bg-nomad-primary px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50">
                            {{ loading ? 'Sending...' : 'Send message' }}
                            <ArrowRight class="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </Container>
    <Container>
        <C2a title="Hourly Support Subscription"
            subtitle="Access Dedicated Assistance and Support at an Average Rate of $72.8 This Year" cta="Pay now"
            url="/" />
    </Container>
</template>
<script>
import axios from 'axios'
import C2a from '@/components/C2a.vue';
import Container from '@/layouts/Container.vue';
import { ArrowRight, Mail, MapPin, Phone } from '@lucide/vue';


export default {
    components: {
        C2a,
        Container,
        ArrowRight,
        Mail,
        MapPin,
        Phone,
    },
    data() {
        return {
            loading: false,
            form: {
                fullName: null,
                email: '',
                phone: '',
                message: ''
            },
            message: null,
            errors: null,
            contents: [
                {
                    id: 1,
                    title: 'Research & planing',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    icon: '/assets/icons/star.svg'
                },
                {
                    id: 2,
                    title: 'Design & Development',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    icon: '/assets/icons/code.svg'
                },
                {
                    id: 3,
                    title: 'SEO & Marketing',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    icon: '/assets/icons/zap-off.svg'
                }
            ],
            projects: [
                {
                    id: 1,
                    name: 'Flex Cleaning Service',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals. being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    color: '#D6D3E2',
                    url: '',
                    image: '/assets/images/screen-fc.png',
                    stats: [{ number: '5x', context: 'branches' }, { number: '700K', context: 'transactions' }]
                },
                {
                    id: 2,
                    name: 'Horn Daily',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals. being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    color: '#D5CB97',
                    url: '',
                    image: '/assets/images/screen-hd.png',
                    stats: [{ number: '200k', context: 'articles' }, { number: '150K', context: 'visits' }]
                },
                {
                    id: 3,
                    name: 'Nomad Lyrics',
                    description: 'Our cross-functional design, development and business analysis team is ready to take on your next project, being guided by in-depth tech expertise, data-driven insights and your business goals. being guided by in-depth tech expertise, data-driven insights and your business goals.',
                    color: '#97D5B4',
                    url: '',
                    image: '/assets/images/screen-discover.png',
                    stats: [{ number: '1.2M', context: 'streams' }, { number: '100K', context: 'visits' }]
                }
            ]
        }
    },
    methods: {
        _submit() {
            this.loading = true
            axios.post('/send_mail', this.form)
                .then((response) => {
                    this.loading = false
                    this.message =  response.data.message
                    this.errors = null
                    this.form  = {
                            fullName: null,
                            email: null,
                            phone: null,
                            message: null
                        }
                })
                .catch((error) => {
                    this.loading = false
                    this.errors = error.response.data
                })
        }
    }
}
</script>